import { Check } from "@phosphor-icons/react";

/**
 * StepCard — a single progressive-disclosure step in the simulator flow.
 * States: locked (dim), active (highlighted), or done (compact w/ check).
 */
export default function StepCard({
  step,
  title,
  description,
  state = "locked", // "locked" | "active" | "done"
  children,
  summary,
  testId,
  onEdit,
}) {
  if (state === "locked") {
    return (
      <div
        data-testid={testId}
        className="glass rounded-2xl p-4 opacity-40 pointer-events-none"
      >
        <div className="flex items-center gap-3">
          <StepDot step={step} state="locked" />
          <div>
            <div className="text-sm font-medium text-slate-400">{title}</div>
            <div className="text-[11px] text-slate-500">{description}</div>
          </div>
        </div>
      </div>
    );
  }

  if (state === "done") {
    return (
      <div
        data-testid={testId}
        className="glass rounded-2xl p-4 flex items-center gap-3 hover:bg-white/[0.03] transition-colors"
      >
        <StepDot step={step} state="done" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white truncate">{title}</div>
          <div className="text-[11px] text-slate-400 truncate">{summary}</div>
        </div>
        {onEdit && (
          <button
            onClick={onEdit}
            className="text-[11px] text-pink-300 hover:text-pink-200 px-2 py-1 rounded-md hover:bg-white/[0.05]"
          >
            Edit
          </button>
        )}
      </div>
    );
  }

  // active
  return (
    <div
      data-testid={testId}
      className="relative glass rounded-2xl p-4 sm:p-5 ring-1 ring-pink-500/30 shadow-lg shadow-pink-500/10 fade-up"
    >
      <div className="absolute -inset-px rounded-2xl pointer-events-none opacity-40"
           style={{ background: "linear-gradient(135deg, rgba(236,72,153,0.15), transparent 60%)" }} />
      <div className="relative flex items-start gap-3 mb-4">
        <StepDot step={step} state="active" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-pink-300 font-mono">
            Step {step} of 11
          </div>
          <div className="mt-0.5 font-display text-lg font-semibold text-white">
            {title}
          </div>
          <div className="text-xs text-slate-400 mt-0.5">{description}</div>
        </div>
      </div>
      <div className="relative">{children}</div>
    </div>
  );
}

function StepDot({ step, state }) {
  const styles =
    state === "done"
      ? "bg-gradient-to-br from-pink-500 to-orange-500 text-white"
      : state === "active"
      ? "bg-white/[0.08] text-pink-300 ring-1 ring-pink-400/40"
      : "bg-white/[0.03] text-slate-500 ring-1 ring-white/[0.08]";
  return (
    <div
      className={`h-8 w-8 flex-shrink-0 rounded-full flex items-center justify-center font-mono text-xs font-semibold ${styles}`}
    >
      {state === "done" ? <Check weight="bold" className="h-4 w-4" /> : step}
    </div>
  );
}
